import { create } from 'zustand';
import { api, ApiError, rateBudget, type GetPaths } from '@/api/client';
import { queryClient } from '@/queryClient';
import { ALL_SKUS_KEY, holdingReadKey, type SkuRow } from '@/hooks/inventory';
import { useAuth } from '@/stores/auth';
import { useConnection } from '@/stores/connection';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { setAfterReferenceSent } from './engine';
import { stockChanged } from './feed';
import type { OutboxHolder } from './outbox';

/**
 * What every Inventory & Stores tab needs, loaded before the line goes
 * (.docs/023, step 5).
 *
 * Without this, offline a screen could only show what that PC had happened to
 * open in the last week — and a Stock OUT for an item whose lots had never
 * been looked at could not be planned at all. So, while there is a line, this
 * walks what the person may see and puts it in the query cache under exactly
 * the keys the screens ask for. The cache lives in the person's sealed copy,
 * so it is there after a reload with no line.
 *
 * Gently: one request at a time, stopping the moment the line goes, and never
 * in the way of a screen, which asks for its own data as it always has.
 */

/** Matches the page every document list asks for first. */
const LIST_PAGE = 50;
/** The most balance rows the walk over every store reads, a page at a time. */
const BALANCE_PAGE = 200;
const BALANCE_CEILING = 50_000;
/** The most items the API gives in one page, and the most this ever reads. */
const SKU_PAGE = 200;
const SKU_CEILING = 20_000;
/** How often to load it all again while the line holds, whatever the feed says. */
export const WARM_EVERY_MS = 30 * 60_000;
/** How often to ask the change feed whether anything stock-related moved. */
export const FEED_EVERY_MS = 2 * 60_000;

interface WarmStatus {
  /** Requests done and planned in the current walk; both 0 when idle. */
  done: number;
  total: number;
  /** When the last walk finished, for "This PC" and the offline bar. */
  finishedAt: number | null;
}

export const useWarmStatus = create<WarmStatus>(() => ({ done: 0, total: 0, finishedAt: null }));

type Step = () => Promise<void>;

interface BalanceRow {
  storeId: string;
  skuId: string;
  batchId: string | null;
  isBatchTracked: boolean;
  qty: string;
}

let running: AbortController | null = null;

/**
 * Starts a walk. One already going is left to finish — paced, a walk over
 * many stores can take minutes, and starting again each time would mean it
 * never got to the end — and another follows it, so what changed meanwhile
 * is read too.
 */
export function warmInventory(): Promise<void> {
  if (running) {
    again = true;
    return Promise.resolve();
  }
  const controller = new AbortController();
  running = controller;
  return walk(controller.signal).finally(() => {
    if (running !== controller) return;
    running = null;
    if (again && !controller.signal.aborted) {
      again = false;
      void warmInventory();
    }
  });
}

let again = false;

// An item, unit or category added offline has just been made: the pickers'
// list of every item is read again, with its real code.
setAfterReferenceSent(() => void warmInventory());

let timer: ReturnType<typeof setInterval> | null = null;
let feedTimer: ReturnType<typeof setInterval> | null = null;
let unsubscribe: (() => void) | null = null;

/**
 * Keeps it loaded while somebody is signed in online: now, every half hour,
 * the moment the line comes back after it went, and whenever the change feed
 * says something a stock tab shows has moved. `store` keeps the feed's place.
 */
export function startWarming(store?: Pick<OutboxHolder, 'read' | 'write'>): void {
  stopWarming();
  void warmInventory();
  timer = setInterval(() => void warmInventory(), WARM_EVERY_MS);
  if (store) feedTimer = setInterval(() => void followFeed(store), FEED_EVERY_MS);
  unsubscribe = useConnection.subscribe((state, previous) => {
    if (state.online && !previous.online) void warmInventory();
  });
}

/** Stops the walk. At sign-out. */
export function stopWarming(): void {
  running?.abort();
  running = null;
  again = false;
  if (timer) clearInterval(timer);
  timer = null;
  if (feedTimer) clearInterval(feedTimer);
  feedTimer = null;
  unsubscribe?.();
  unsubscribe = null;
  useWarmStatus.setState({ done: 0, total: 0 });
}

async function walk(signal: AbortSignal): Promise<void> {
  const can = useAuth.getState().can;
  const farmIds = useFarmScope.getState().selectedFarmIds;
  const scope = farmScopeQuery(farmIds);

  // What is needed to *save* comes first: stores, suppliers, every item (for
  // the item pickers' search), and what each store holds, lot by lot. Then
  // the lists, newest first.
  const first: Step[] = [];
  const later: Step[] = [];
  let stores: Array<{ id: string; isActive?: boolean; farmId?: string | null }> = [];

  if (can('inv.store.read') || can('inv.inward.read') || can('inv.consumption.read')) {
    first.push(async () => {
      stores = (await prefetch(['stores'], () => api.get('/api/v1/stores'))) as typeof stores;
    });
  }
  if (can('inv.supplier.read') || can('inv.inward.create')) {
    first.push(async () => {
      await prefetch(['suppliers'], () => api.get('/api/v1/suppliers'));
    });
  }

  if (can('inv.sku.read')) first.push(() => allItems(signal));

  const list = (permission: string, key: unknown[], path: string, query: Record<string, string | number>) => {
    if (!can(permission)) return;
    later.push(async () => {
      await prefetch(key, () => api.get(path as GetPaths, { take: LIST_PAGE, skip: 0, ...query }));
    });
  };
  list('inv.consumption.read', ['inv', 'consumption', farmIds, 0], '/api/v1/inventory/consumption', scope);
  list('inv.inward.read', ['inv', 'inward', farmIds, 0], '/api/v1/inventory/inward', scope);
  list('inv.transfer.read', ['inv', 'transfers', farmIds, 0], '/api/v1/inventory/transfers', scope);
  list('inv.adjustment.read', ['inv', 'adjustments', farmIds, 0], '/api/v1/inventory/adjustments', { ...scope });
  list('inv.demand.read', ['inv', 'demands', farmIds, 'open', 0], '/api/v1/inventory/demands', { status: 'open', ...scope });

  // The Stock screen and the Items Manager as they first open, under the keys
  // they read (src/screens/Workbench.tsx, src/screens/Reference.tsx).
  const view = (permission: string, key: unknown[], path: string, query?: Record<string, string | number>) => {
    if (!can(permission)) return;
    later.push(async () => {
      await prefetch(key, () => api.get(path as GetPaths, query));
    });
  };
  const allStores = { ...scope, groupBy: 'sku', take: 100, skip: 0 };
  view('inv.dashboard.read', ['inv', 'balances', allStores], '/api/v1/inventory/balances', allStores);
  view('inv.dashboard.read', ['inv', 'dashboard', farmIds], '/api/v1/inventory/dashboard', scope);
  view('inv.sku.read', ['inv', 'categories', false], '/api/v1/inventory/categories');
  view('inv.sku.read', ['inv', 'uoms', false], '/api/v1/inventory/uoms');
  view('inv.sku.read', ['inv', 'skus', 'reference', { take: LIST_PAGE, skip: 0 }], '/api/v1/inventory/skus', { take: LIST_PAGE, skip: 0 });
  view('inv.sku.read', ['inv', 'categories', 'reference', false], '/api/v1/inventory/categories');
  view('inv.store.read', ['stores', 'reference', false], '/api/v1/stores');
  view('inv.supplier.read', ['suppliers', 'reference', '', false], '/api/v1/suppliers');

  useWarmStatus.setState({ done: 0, total: first.length + later.length });
  if (!(await run(first, signal))) return;

  // What every store holds, read once in full — a page of 200 rows at a time
  // across all stores, not a request per store, which on a company with
  // hundreds of stores used up the server's allowance of requests on its own
  // — and laid out the way the line pickers ask for it: per item, per lot,
  // and the lots of every tracked item.
  if (can('inv.dashboard.read') || can('inv.consumption.create') || can('inv.transfer.create')) {
    let tracked: Array<{ storeId: string; skuId: string }> = [];
    useWarmStatus.setState((s) => ({ total: s.total + 1 }));
    if (!(await run([async () => { tracked = await holdings(stores, farmIds, scope, signal); }], signal))) return;
    const lots: Step[] = tracked.map(({ storeId, skuId }) => async () => {
      await prefetch(['inv', 'batches', storeId, skuId], () => api.get('/api/v1/inventory/batches', { storeId, skuId }));
    });
    useWarmStatus.setState((s) => ({ total: s.total + lots.length }));
    if (!(await run(lots, signal))) return;
  }

  if (!(await run(later, signal))) return;
  useWarmStatus.setState({ done: 0, total: 0, finishedAt: Date.now() });
}

/**
 * Requests left for the person's own screens in each window of the server's
 * rate limit. The warm-up is background work: it waits for the next window
 * rather than spend these, so it never makes a screen fail with "too busy".
 */
const KEEP_FOR_SCREENS = 100;
/** A 429 is waited out and the step tried again, this many times. */
const BUSY_TRIES = 3;

/** Waits, unless stopped. */
function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });
}

/** Holds back while the server's allowance is nearly spent, until it resets. */
async function politely(signal: AbortSignal): Promise<void> {
  const budget = rateBudget();
  if (budget && budget.remaining <= KEEP_FOR_SCREENS && budget.resetAt > Date.now()) {
    await pause(budget.resetAt - Date.now() + 250, signal);
  }
}

/** Runs steps in turn. False when it stopped: aborted, or the line went. */
async function run(steps: readonly Step[], signal: AbortSignal): Promise<boolean> {
  for (const step of steps) {
    for (let tries = 1; ; tries += 1) {
      if (signal.aborted) return false;
      await politely(signal);
      if (signal.aborted) return false;
      try {
        await step();
        break;
      } catch (error) {
        // No line: stop, and try again when it is back.
        if (error instanceof ApiError && (error.status === 0 || error.status === 502 || error.status === 504)) return false;
        // Too busy: wait as long as the server asks, then the same step again.
        if (error instanceof ApiError && error.status === 429 && tries < BUSY_TRIES) {
          await pause(error.retryAfterMs ?? 30_000, signal);
          continue;
        }
        // Anything else — a permission the screen would also be refused — is skipped.
        break;
      }
    }
    useWarmStatus.setState((s) => ({ done: s.done + 1 }));
  }
  return !signal.aborted;
}

function prefetch(key: unknown[], fetch: () => Promise<unknown>): Promise<unknown> {
  // `run` decides about failures, waiting out a busy server itself.
  return queryClient.fetchQuery({ queryKey: key, queryFn: fetch, staleTime: 0, retry: false });
}

/** Every active item, a page at a time, for the item pickers' search. */
async function allItems(signal: AbortSignal): Promise<void> {
  const rows: SkuRow[] = [];
  for (let skip = 0; skip < SKU_CEILING; skip += SKU_PAGE) {
    const page = await api.get('/api/v1/inventory/skus', { take: SKU_PAGE, skip });
    rows.push(...page.items);
    if (page.items.length < SKU_PAGE || rows.length >= page.total) break;
    await politely(signal);
    if (signal.aborted) return;
  }
  queryClient.setQueryData(ALL_SKUS_KEY, rows);
}

/**
 * What every store in view holds, and what the line pickers ask about it: how
 * much of each item is held in each store, and of each lot (`useOnHand`).
 * Returns the tracked items each store holds, whose lots are then asked of
 * the server itself (`useBatches`), so they come in its order, oldest first,
 * with their received dates.
 */
async function holdings(
  stores: ReadonlyArray<{ id: string; isActive?: boolean; farmId?: string | null }>,
  farmIds: readonly string[],
  scope: Record<string, string>,
  signal: AbortSignal,
): Promise<Array<{ storeId: string; skuId: string }>> {
  const rows: BalanceRow[] = [];
  let total = Infinity;
  for (let skip = 0; skip < BALANCE_CEILING && rows.length < total; skip += BALANCE_PAGE) {
    if (skip > 0) await politely(signal);
    if (signal.aborted) return [];
    const page = (await api.get('/api/v1/inventory/balances', {
      ...scope,
      groupBy: 'lot',
      take: BALANCE_PAGE,
      skip,
    })) as unknown as { items: BalanceRow[]; total: number };
    rows.push(...page.items);
    total = page.total;
    if (page.items.length < BALANCE_PAGE) break;
  }

  const byItem = new Map<string, number>();
  const tracked = new Map<string, { storeId: string; skuId: string }>();
  const seen = new Set<string>();
  for (const row of rows) {
    const key = `${row.storeId}:${row.skuId}`;
    seen.add(row.storeId);
    byItem.set(key, (byItem.get(key) ?? 0) + Number(row.qty));
    if (row.batchId) queryClient.setQueryData(['inv', 'balances', 'onhand', row.storeId, row.skuId, row.batchId], Number(row.qty));
    if (row.isBatchTracked) tracked.set(key, { storeId: row.storeId, skuId: row.skuId });
  }
  for (const [key, qty] of byItem) {
    const [storeId, skuId] = key.split(':') as [string, string];
    queryClient.setQueryData(['inv', 'balances', 'onhand', storeId, skuId, null], qty);
  }

  // Read in full: offline, an item not in it is held at zero there. With no
  // farm chosen that is every store; with farms chosen, theirs.
  if (!signal.aborted && rows.length >= total) {
    const at = Date.now();
    for (const store of stores) {
      if (store.isActive === false) continue;
      const inView = farmIds.length === 0 || seen.has(store.id) || (store.farmId != null && farmIds.includes(store.farmId));
      if (inView) queryClient.setQueryData(holdingReadKey(store.id), at);
    }
  }
  return [...tracked.values()];
}

/**
 * One look at the change feed. When stock data moved elsewhere, the screens
 * open now read it again at once, and the warm-up brings the rest up to date.
 */
export async function followFeed(store: Pick<OutboxHolder, 'read' | 'write'>): Promise<void> {
  if (!useConnection.getState().online) return;
  let changed: boolean;
  try {
    changed = await stockChanged(store);
  } catch {
    return; // No line, or the feed is unavailable: the half-hourly load still runs.
  }
  if (!changed) return;
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['inv'] }),
    queryClient.invalidateQueries({ queryKey: ['stores'] }),
    queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
  ]);
  void warmInventory();
}
